import OrderBlockedNotice from '../../components/OrderDocumentsStep/OrderBlockedNotice';
import OrderDocumentsStep, {
  documentsAnswered,
  needsAnswer,
} from '../../components/OrderDocumentsStep/OrderDocumentsStep';
import SubmittedDocuments from '../../components/OrderDocumentsStep/SubmittedDocuments';
import { useHistory } from 'react-router-dom';
import { useToast } from '../../components/Toast/ToastContext';
import { useExemptionDocumentUpload } from '../Account/useExemptionDocumentUpload';
import {
  rescueOrderDocumentRequirementsQuery,
  type RescueOrderDocumentRequirementsData,
  type RescueOrderDocumentRequirementsVariables,
} from '../../lib/documentQueries';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import type { AppState } from '../../../lib/store';
import { useRescueContext } from '../../lib/RescueContext';
import { SUPPORT_EMAIL } from '../../lib/emails';
import { EMAIL_RE, formatDate, US_STATE_OPTIONS } from '../../lib/utils';
import {
  rescueCreateDeviceOrderMutation,
  RescueCreateDeviceOrderData,
  RescueCreateDeviceOrderInput,
  RescueDevicesProgramData,
  rescueDevicesProgramQuery,
} from '../../lib/rescueQueries';
import {
  trackDevicesAttestationAgreed,
  trackDevicesAttestationViewed,
  trackDevicesOrderDetailsCompleted,
  trackDevicesOrderSubmitted,
} from '../../analytics';
import { RescueDeviceOrderAttestationVariant, ResellerTaxExemptType } from '@/types/gql-op-types';
import StepProgress from '../../components/StepProgress/StepProgress';
import OrderAttestationStep from '../../components/OrderAttestationStep/OrderAttestationStep';
import stepStyles from '../../components/OrderAttestationStep/orderStepModal.module.scss';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput, PortalTextarea } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { ToggleGroup } from '../../components/FormField/ToggleGroup';
import { FormGrid } from '../../components/FormField/FormGrid';
import Button from '../../components/Button/Button';
import styles from './Devices.module.scss';

const QUANTITY_PRESETS = ['10', '20', '30', '40', '50', 'Other'] as const;
type QuantityChoice = (typeof QUANTITY_PRESETS)[number];
const DEFAULT_QUANTITY: QuantityChoice = '10';
const CUSTOM_QUANTITY_STEP = 10;
const CUSTOM_QUANTITY_MIN = 60;
const CUSTOM_QUANTITY_MAX = 100;
/** A first order is a fixed batch. Mirrors STARTER_BATCH_DEVICE_QUANTITY in web, which enforces it. */
const STARTER_BATCH_QUANTITY = 10;

interface OrderDevicesModalProps {
  open: boolean;
  onClose: () => void;
}

interface SuccessState {
  orderId: string;
  deviceCount: number;
  includeHardCases: boolean;
}

const STEP_LABELS = { 1: 'Order details', 2: 'Documents', 3: 'Agree & Continue' } as const;

function documentsTitle(step: 1 | 2 | 3, government: boolean): string {
  switch (step) {
    case 1:
      return 'Order Fi Devices';
    case 2:
      return government ? 'Government status' : 'Nonprofit documents';
    case 3:
      return 'Program commitment';
  }
}

/** A rescue that has ordered before is told Fi now needs the letter, rather than that this is its first order. */
function documentsIntro(isStarterBatch: boolean, government: boolean): string {
  const later = 'Future orders require document approval.';
  if (government) {
    return `Fi offers free Fi Minis to nonprofits and government shelters, so we need proof of your shelter's government status on file. ${
      isStarterBatch ? "Your first Fi order won't" : "This order won't"
    } be held up while we review this. ${later}`;
  }
  return isStarterBatch
    ? `Fi offers free Fi Minis to registered nonprofits, so we need your IRS determination letter on file. Your first Fi order won't be held up while we review this. ${later}`
    : `Fi now needs your IRS determination letter on file for Fi Mini orders. This order won't be held up while we review this. ${later}`;
}

export default function OrderDevicesModal({ open, onClose }: OrderDevicesModalProps) {
  const { orgName, shippingAddress, taxExemptType } = useRescueContext();
  const session = useSelector((state: AppState) => state.session);
  // Refetch the attestation after a successful order: placing one flips a first-order rescue to the
  // standing terms server-side, and without this the next order reads a cached STARTER_BATCH and
  // submits a version the server no longer accepts.
  //
  // Named explicitly rather than `refetchQueries: 'active'`. A blanket refetch also reloads the
  // device queries, and for a rescue with no devices yet that satisfies the Devices page's
  // `initialLoading` condition — which early-returns a spinner, unmounting this modal mid-flow and
  // destroying the confirmation the rescue just earned.
  const [createOrder] = useMutation<RescueCreateDeviceOrderData, { input: RescueCreateDeviceOrderInput }>(
    rescueCreateDeviceOrderMutation,
    { refetchQueries: [{ query: rescueDevicesProgramQuery }] },
  );
  // Fetched by the Devices page too, so this normally resolves from cache rather than re-requesting.
  const { data: programData, refetch: refetchProgram } = useQuery<RescueDevicesProgramData>(rescueDevicesProgramQuery);
  const attestation = programData?.rescueDevicesProgram.deviceOrderAttestation;
  // An order the rescue has already filed and Fi hasn't reviewed yet. Only one may be outstanding,
  // so this replaces the form rather than sitting alongside it.
  const pendingOrder = programData?.rescueDevicesProgram.pendingDeviceOrder ?? null;

  // Only pre-fill from the rescue's stored address when it's actually a clean
  // US address with a 2-letter state code that matches our `<select>` options.
  // Anything else (non-US country, full state name like "New York", missing
  // state) falls back to a blank form so the user can't accidentally submit a
  // country-corrupted order or get confused by an empty State select.
  // Memoized so the address-seed useEffect's dep array stays stable across
  // re-renders that don't actually change the underlying address.
  const validUSAddress = useMemo(() => {
    if (!shippingAddress) {
      return null;
    }
    if (shippingAddress.country !== 'US') {
      return null;
    }
    if (!shippingAddress.state || !US_STATE_OPTIONS.some((s) => s.abbreviation === shippingAddress.state)) {
      return null;
    }
    return shippingAddress;
  }, [shippingAddress]);

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState(session?.email ?? '');
  const [quantityChoice, setQuantityChoice] = useState<QuantityChoice>(DEFAULT_QUANTITY);
  const [customQuantity, setCustomQuantity] = useState(CUSTOM_QUANTITY_MIN);
  const [includeHardCases, setIncludeHardCases] = useState(true);
  const [street, setStreet] = useState(validUSAddress?.line1 ?? '');
  const [apt, setApt] = useState(validUSAddress?.line2 ?? '');
  const [city, setCity] = useState(validUSAddress?.city ?? '');
  const [usState, setUsState] = useState(validUSAddress?.state ?? '');
  const [zip, setZip] = useState(validUSAddress?.zip ?? '');
  const [notes, setNotes] = useState('');
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [documentsInPlay, setDocumentsInPlay] = useState(false);

  // Fi Devices collects the federal letter and no state document, so the ship-to never matters here.

  const { data: requirementsData, refetch: refetchRequirements } = useQuery<
    RescueOrderDocumentRequirementsData,
    RescueOrderDocumentRequirementsVariables
  >(rescueOrderDocumentRequirementsQuery, {
    variables: { input: { program: 'FI_DEVICES', shipToState: null } },
    skip: !open,
    fetchPolicy: 'cache-and-network',
  });
  const requirements = requirementsData?.rescueOrderDocumentRequirements ?? null;
  const history = useHistory();
  const { showToast } = useToast();
  const [justReplaced, setJustReplaced] = useState(false);
  const { fileInputRef, handleFileSelected, pickFileFor, fileError, uploadingFor, keyFor } = useExemptionDocumentUpload(
    (documentType) => {
      void refetchRequirements();
      // Filed from inside this modal, so the wait that follows is news rather than a refusal.
      if (blocked) {
        setJustReplaced(true);
        showToast(
          `${documentType === 'NONPROFIT_LETTER' ? 'Letter' : 'Certificate'} uploaded. The Fi team will review it within a few business days.`,
        );
      }
    },
  );
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SuccessState | null>(null);

  // Re-seed the address fields once per open cycle. Handles the case where the
  // org-info query resolves after the modal has already mounted.
  //
  // Known race: if the user opens the modal *and* starts typing in an address
  // field *before* org-info resolves, this effect will overwrite their input
  // when the data arrives. The window is small in practice — org-info is
  // fetched on app shell mount, well before anyone clicks "Order Devices" —
  // so we accept it. If it ever causes real complaints, the fix is to flip
  // `seededAddressRef.current` to true on the first user keystroke in any
  // address field.
  const seededAddressRef = useRef(false);
  // Same once-per-open-cycle pattern for the contact email. `useState` only
  // captures `session?.email` at first render; if the Redux session resolves
  // after the modal has already mounted (rare but possible), the effect seeds
  // it when it arrives. Same accepted race as the address: typing before the
  // session resolves can be overwritten.
  const seededEmailRef = useRef(false);
  // The order the agreement was given against. An attestation is consent to a specific order, so
  // editing the order after agreeing invalidates it — otherwise a rescue could agree to 10 with
  // cases, go back, change it to 50 without, and submit under the earlier agreement.
  const agreedSignatureRef = useRef<string | null>(null);
  // Fires the attestation-viewed event once per open cycle rather than on every arrival at step 2.
  // The event is the denominator for terms refusal, so counting an Edit → Continue round trip twice
  // would inflate it and make refusal look worse than it is.
  const viewedAttestationRef = useRef(false);
  // Synchronous guard against double-submit. The `submitting` state alone
  // can't prevent it: two rapid clicks landing before React commits the
  // first `setSubmitting(true)` would both pass `disabled={submitting}`.
  const submittingRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededAddressRef.current = false;
      viewedAttestationRef.current = false;
      return;
    }
    if (validUSAddress && !seededAddressRef.current) {
      setStreet(validUSAddress.line1 ?? '');
      setApt(validUSAddress.line2 ?? '');
      setCity(validUSAddress.city ?? '');
      setUsState(validUSAddress.state ?? '');
      setZip(validUSAddress.zip ?? '');
      seededAddressRef.current = true;
    }
  }, [open, validUSAddress]);
  useEffect(() => {
    if (!open) {
      seededEmailRef.current = false;
      return;
    }
    if (session?.email && !seededEmailRef.current) {
      setContactEmail(session.email);
      seededEmailRef.current = true;
    }
  }, [open, session?.email]);

  function resetForm() {
    setContactName('');
    setContactEmail(session?.email ?? '');
    setQuantityChoice(DEFAULT_QUANTITY);
    setCustomQuantity(CUSTOM_QUANTITY_MIN);
    setIncludeHardCases(true);
    setStreet(validUSAddress?.line1 ?? '');
    setApt(validUSAddress?.line2 ?? '');
    setCity(validUSAddress?.city ?? '');
    setUsState(validUSAddress?.state ?? '');
    setZip(validUSAddress?.zip ?? '');
    setNotes('');
    setStep(1);
    setAgreed(false);
    agreedSignatureRef.current = null;
    setShowErrors(false);
    setSubmitError(null);
    setSubmitting(false);
    setSuccess(null);
    // Belt-and-suspenders: ensure the synchronous gate is clean for the next
    // open even if a close ever slipped through while a submit was in flight.
    submittingRef.current = false;
    setJustReplaced(false);
  }

  function handleClose() {
    // Read the synchronous ref, not the React `submitting` state. A backdrop
    // / Escape / ✕ click landing right after Submit (before React has
    // committed `setSubmitting(true)`) would otherwise sneak past, letting the
    // modal close mid-mutation and triggering late `setSuccess` / `setSubmitError`
    // updates after unmount.
    if (submittingRef.current) {
      return;
    }
    resetForm();
    onClose();
  }

  const variant = attestation?.variant ?? RescueDeviceOrderAttestationVariant.STANDING;
  const isStarterBatch = variant === RescueDeviceOrderAttestationVariant.STARTER_BATCH;

  const isOther = quantityChoice === 'Other';
  // A first order is fixed at 10 with cases included; neither control is rendered, and the server
  // rejects a submission that disagrees.
  const deviceCount = isStarterBatch
    ? STARTER_BATCH_QUANTITY
    : isOther
      ? customQuantity
      : Number.parseInt(quantityChoice, 10);
  const effectiveIncludeHardCases = isStarterBatch ? true : includeHardCases;
  const trimmedEmail = contactEmail.trim();
  const addressSummary = [street.trim(), [city.trim(), usState].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  // What the rescue is agreeing to, as shown on the step 2 recap.
  const orderSignature = `${deviceCount}|${effectiveIncludeHardCases}|${addressSummary}`;

  const errors = {
    contactName: contactName.trim() === '',
    contactEmailMissing: trimmedEmail === '',
    contactEmailInvalid: trimmedEmail !== '' && !EMAIL_RE.test(trimmedEmail),
    deviceCount: !Number.isFinite(deviceCount) || deviceCount <= 0,
    street: street.trim() === '',
    city: city.trim() === '',
    state: usState === '',
    zip: zip.trim() === '',
  };
  const hasErrors = Object.values(errors).some(Boolean);

  useEffect(() => {
    if (agreed && agreedSignatureRef.current !== null && agreedSignatureRef.current !== orderSignature) {
      setAgreed(false);
      agreedSignatureRef.current = null;
    }
  }, [agreed, orderSignature]);

  useEffect(() => {
    if (step === 3 && !viewedAttestationRef.current) {
      viewedAttestationRef.current = true;
      trackDevicesAttestationViewed(variant);
    }
  }, [step, variant]);

  /** No upload clears this one — a document already in review, or a jurisdiction with no position. */
  const blocked = requirements !== null && !requirements.canOrder;
  /** The documents step exists only when something needs uploading; a letter merely in review is enough. */
  const needsDocuments = !blocked && (requirements?.required ?? []).some(needsAnswer);
  const answered = documentsAnswered(requirements?.required ?? []);
  // Settled when they leave step 1, so an upload on the documents step doesn't renumber the steps.
  const withDocuments = step === 1 ? needsDocuments : documentsInPlay;
  const totalSteps = withDocuments ? 3 : 2;
  const government = taxExemptType === ResellerTaxExemptType.GOVERNMENT_ENTITY;

  /** Step 1 → 2. The attestation is a discrete step so terms refusal is measurable on its own. */
  function handleContinue() {
    if (hasErrors) {
      setSubmitError(null);
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setDocumentsInPlay(needsDocuments);
    setStep(needsDocuments ? 2 : 3);
    trackDevicesOrderDetailsCompleted(variant, deviceCount);
  }

  function handleAgreedChange(next: boolean) {
    setAgreed(next);
    agreedSignatureRef.current = next ? orderSignature : null;
    if (next) {
      trackDevicesAttestationAgreed(variant);
    }
  }

  async function handleSubmit() {
    if (submittingRef.current) {
      return;
    }
    if (hasErrors) {
      // Reachable only if step 1 became invalid after it was validated. Say so rather than silently
      // teleporting the rescue back a step with no explanation.
      setShowErrors(true);
      setSubmitError('Some order details need attention before this order can be placed.');
      setStep(1);
      return;
    }
    if (!agreed || !attestation) {
      return;
    }
    submittingRef.current = true;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const result = await createOrder({
        variables: {
          input: {
            contactName: contactName.trim(),
            contactEmail: trimmedEmail,
            deviceQuantity: deviceCount,
            includeHardCases: effectiveIncludeHardCases,
            shippingAddress: {
              line1: street.trim(),
              line2: apt.trim() ? apt.trim() : null,
              city: city.trim(),
              state: usState,
              // US-only — the form's state list is US states, so country is
              // hardcoded. Schema requires a non-null country string.
              country: 'US',
              zip: zip.trim(),
            },
            notes: notes.trim() ? notes.trim() : null,
            attestationVersion: attestation!.version,
            attestationAgreed: true,
          },
        },
      });
      const data = result.data?.rescueCreateDeviceOrder;
      if (!data?.ok) {
        throw new Error('Order submission was not accepted');
      }
      trackDevicesOrderSubmitted(deviceCount, effectiveIncludeHardCases, variant);
      setSuccess({ orderId: data.orderId, deviceCount, includeHardCases: effectiveIncludeHardCases });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit order request');
      // `refetchQueries` on the mutation only runs on success, and one of the ways this can fail is
      // the server finding an order already awaiting approval — filed in another tab, or by another
      // staff member. Re-read the program so the modal swaps to the blocked state instead of
      // leaving a filled-in form under an error the rescue can't act on.
      void refetchProgram().catch(() => {
        // Already showing a submit error; a failed refetch has nothing to add.
      });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  function decrementCustom() {
    setCustomQuantity((prev) => Math.max(CUSTOM_QUANTITY_MIN, prev - CUSTOM_QUANTITY_STEP));
  }

  function incrementCustom() {
    setCustomQuantity((prev) => Math.min(CUSTOM_QUANTITY_MAX, prev + CUSTOM_QUANTITY_STEP));
  }

  if (success) {
    const { orderId, deviceCount: confirmedCount, includeHardCases: confirmedHardCases } = success;
    return (
      <Modal open={open} onClose={handleClose} size="wide">
        <div className={styles.orderConfirmState}>
          <div className={styles.orderConfirmIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M5 12.5L10 17.5L19 7.5"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className={styles.orderConfirmTitle}>Request Submitted</h2>
          <p className={styles.orderConfirmDesc}>
            Your order for{' '}
            <strong>
              {confirmedCount} Fi Mini device{confirmedCount === 1 ? '' : 's'}
            </strong>
            {confirmedHardCases ? ' with hard cases' : ''} has been submitted for approval. You'll receive an email
            confirmation once approved.
          </p>
          <SubmittedDocuments required={requirements?.required ?? []} />

          <div className={styles.orderConfirmFooter}>
            <div className={styles.orderConfirmOrderNumber}>Order #{orderId}</div>
          </div>
        </div>
      </Modal>
    );
  }

  // Checked after `success` so a rescue who just ordered still sees their confirmation — the
  // refetch that follows a successful order is what puts their own new order here.
  if (pendingOrder) {
    return (
      <Modal open={open} onClose={handleClose} size="wide">
        <div className={styles.orderConfirmState}>
          <div className={`${styles.orderConfirmIcon} ${styles.orderPendingIcon}`} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
              <path d="M12 7.5V12L15 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className={styles.orderConfirmTitle}>Order Awaiting Approval</h2>
          <p className={styles.orderConfirmDesc}>{pendingOrder.message}</p>
          <div className={styles.orderConfirmFooter}>
            {/* The served copy already names the order, so this adds only what it leaves out. */}
            <div className={styles.orderConfirmOrderNumber}>Submitted {formatDate(pendingOrder.submittedAt)}</div>
          </div>
          <ModalActions>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </ModalActions>
        </div>
      </Modal>
    );
  }

  if (blocked) {
    return (
      <Modal open={open} onClose={handleClose} size="wide">
        <OrderBlockedNotice
          required={requirements?.required ?? []}
          notices={requirements?.notices ?? []}
          shipToState={null}
          uploadingKey={uploadingFor}
          keyFor={keyFor}
          onPickFile={pickFileFor}
          fileError={fileError}
          programName="Fi Mini"
          justReplaced={justReplaced}
        />
        <ModalActions>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          {/* They came here to be told; the account page is where they can act on it. A rejection is fixed right here. */}
          {!justReplaced && !requirements?.required.some((entry) => entry.document?.status === 'REJECTED') && (
            <Button variant="green" onClick={() => history.push('/rescue/account')}>
              View documents
            </Button>
          )}
        </ModalActions>
      </Modal>
    );
  }

  function footerActions() {
    switch (step) {
      case 1:
        return (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="green" onClick={handleContinue} disabled={!attestation}>
              Continue &rarr;
            </Button>
          </>
        );
      case 2:
        // Something on file is the bar, not approval: a first order ships while Fi reviews it.
        return (
          <>
            <Button variant="secondary" onClick={() => setStep(1)} disabled={submitting}>
              &larr; Back
            </Button>
            <Button variant="green" onClick={() => setStep(3)} disabled={submitting || !answered}>
              Continue &rarr;
            </Button>
          </>
        );
      case 3:
        return (
          <>
            <Button variant="secondary" onClick={() => setStep(withDocuments ? 2 : 1)} disabled={submitting}>
              &larr; Back
            </Button>
            <Button
              variant="green"
              className={stepStyles.agreeSubmit}
              onClick={handleSubmit}
              disabled={submitting || !agreed}
            >
              {submitting ? 'Submitting…' : 'Agree & Submit'}
            </Button>
          </>
        );
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="wide"
      className={stepStyles.orderStepModal}
      dismissOnBackdropClick={false}
    >
      <div className={`${stepStyles.orderStepBody} ${step === 3 ? stepStyles.orderStepBodyPinned : ''}`}>
        <StepProgress
          currentStep={withDocuments ? step : Math.min(step, 2)}
          totalSteps={totalSteps}
          labelSuffix={STEP_LABELS[step]}
        />
        <ModalTitle>{documentsTitle(step, government)}</ModalTitle>

        {step === 1 && (
          <>
            <p className={styles.modalDescription}>
              {isStarterBatch ? 'Request your starter batch for ' : 'Request Fi Mini devices for '}
              <strong>{orgName || 'your rescue'}</strong>. Orders are reviewed and typically approved within 1 business
              day.
            </p>

            <FormGrid>
              <PortalField
                label="Contact Name *"
                error="Please provide a contact name"
                showError={showErrors && errors.contactName}
              >
                <PortalInput
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Full name"
                  hasError={showErrors && errors.contactName}
                />
              </PortalField>
              <PortalField
                label="Contact Email *"
                error={
                  errors.contactEmailMissing ? 'Please provide a contact email' : 'Please provide a valid email address'
                }
                showError={showErrors && (errors.contactEmailMissing || errors.contactEmailInvalid)}
              >
                <PortalInput
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="you@example.org"
                  hasError={showErrors && (errors.contactEmailMissing || errors.contactEmailInvalid)}
                />
              </PortalField>
            </FormGrid>

            {isStarterBatch ? (
              <PortalField label="Number of Devices">
                <div className={styles.lockedQuantityCard}>
                  <span className={styles.lockedQuantityValue}>{STARTER_BATCH_QUANTITY}</span>
                  <span>
                    <span className={styles.lockedQuantityLabel}>Fi Minis &mdash; starter batch</span>
                    <span className={styles.lockedQuantityHint}>
                      Fixed for a first order, shipped with a hard case each. Larger orders open up once you&rsquo;ve
                      transferred 8.
                    </span>
                  </span>
                </div>
              </PortalField>
            ) : (
              <PortalField
                label="Number of Devices *"
                hint='Devices ship in batches of 10, up to 100 per order. Selecting "Other" allows custom quantities in increments of 10.'
              >
                <ToggleGroup
                  options={QUANTITY_PRESETS.map((q) => ({ value: q, label: q }))}
                  value={quantityChoice}
                  onChange={(v) => setQuantityChoice(v as QuantityChoice)}
                />
                {isOther && (
                  <div className={styles.customQuantityRow}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={decrementCustom}
                      disabled={customQuantity <= CUSTOM_QUANTITY_MIN}
                      className={styles.customQuantityButton}
                      aria-label="Decrease quantity"
                    >
                      &minus;
                    </Button>
                    <span className={styles.customQuantityValue}>{customQuantity}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={incrementCustom}
                      disabled={customQuantity >= CUSTOM_QUANTITY_MAX}
                      className={styles.customQuantityButton}
                      aria-label="Increase quantity"
                    >
                      +
                    </Button>
                  </div>
                )}
              </PortalField>
            )}

            {!isStarterBatch && (
              <button
                type="button"
                className={styles.orderCheckboxRow}
                onClick={() => setIncludeHardCases((v) => !v)}
                aria-pressed={includeHardCases}
              >
                <span
                  className={`${styles.orderCheckbox} ${includeHardCases ? styles.orderCheckboxChecked : ''}`}
                  aria-hidden="true"
                >
                  {includeHardCases && (
                    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M3.5 8.5L6.5 11.5L12.5 4.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                <span>
                  <span className={styles.orderCheckboxLabel}>Include hard cases</span>
                  <span className={styles.orderCheckboxHint}>Ship 1 protective hard case per device</span>
                </span>
              </button>
            )}

            <div className={styles.orderSectionHeading}>Shipping Address</div>
            <FormGrid>
              <PortalField
                label="Street Address *"
                fullWidth
                error="Please provide a street address"
                showError={showErrors && errors.street}
              >
                <PortalInput
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  placeholder="123 Main Street"
                  hasError={showErrors && errors.street}
                />
              </PortalField>
              <PortalField label="Apt / Suite">
                <PortalInput value={apt} onChange={(e) => setApt(e.target.value)} placeholder="Apt / Suite" />
              </PortalField>
              <PortalField label="City *" error="Please provide a city" showError={showErrors && errors.city}>
                <PortalInput
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  hasError={showErrors && errors.city}
                />
              </PortalField>
              <PortalField label="State *" error="Please select a state" showError={showErrors && errors.state}>
                <PortalSelect
                  value={usState}
                  onChange={(e) => setUsState(e.target.value)}
                  hasError={showErrors && errors.state}
                >
                  <option value="">Select…</option>
                  {US_STATE_OPTIONS.map((s) => (
                    <option key={s.abbreviation} value={s.abbreviation}>
                      {s.name}
                    </option>
                  ))}
                </PortalSelect>
              </PortalField>
              <PortalField label="Zip Code *" error="Please provide a zip code" showError={showErrors && errors.zip}>
                <PortalInput
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="ZIP"
                  inputMode="numeric"
                  hasError={showErrors && errors.zip}
                />
              </PortalField>
            </FormGrid>

            <PortalField label="Notes">
              <PortalTextarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional — e.g., upcoming adoption event, urgency, special instructions"
              />
            </PortalField>
          </>
        )}

        {step === 2 && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleFileSelected}
              style={{ display: 'none' }}
            />
            <OrderDocumentsStep
              required={requirements?.required ?? []}
              uploadingKey={uploadingFor}
              keyFor={keyFor}
              onPickFile={pickFileFor}
              fileError={fileError}
              intro={documentsIntro(isStarterBatch, government)}
              letterPrograms="Fi Minis"
              government={government}
            />
          </>
        )}

        {step === 3 && attestation && (
          <OrderAttestationStep
            terms={attestation}
            orgName={orgName}
            recapCount={deviceCount}
            recapLabel={`Fi Mini${deviceCount === 1 ? '' : 's'}${effectiveIncludeHardCases ? ' with hard cases' : ''}`}
            addressSummary={addressSummary}
            onEdit={() => setStep(1)}
            agreed={agreed}
            onAgreedChange={handleAgreedChange}
          />
        )}

        {submitError && <div className={styles.errorContainer}>{submitError}</div>}
      </div>

      <div className={`${stepStyles.orderStepFooter} ${stepStyles.orderStepFooterWithSupport}`}>
        <div className={stepStyles.support}>
          Have questions?{' '}
          <a href={`mailto:${SUPPORT_EMAIL}?subject=Fi%20Device%20Order%20Questions`}>Contact support</a>
        </div>
        <ModalActions>{footerActions()}</ModalActions>
      </div>
    </Modal>
  );
}
